import { CloudOff, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';
import { env } from '@/config/env';
import { useSession } from '@/features/auth/session-context';
import { useApiHealth } from '@/features/health/use-api-health';
import styles from './Shell.module.css';

function useBrowserOnline() {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/**
 * "Working offline" banner (#4). RL-720 detects the browser connection and
 * whether the API answers; working through an outage (PLT-01) comes with the
 * domain tasks, so this banner does not promise that changes will sync.
 */
export function OfflineBanner() {
  const online = useBrowserOnline();
  const health = useApiHealth(online && env.appEnv !== 'test', 30_000);
  const apiDown = online && health.isError;
  if (online && !apiDown) return null;
  return (
    <p className={styles.offline} role="alert">
      <CloudOff size={15} aria-hidden="true" />
      {online
        ? 'The clinic server is not answering. Changes cannot be saved until it is back.'
        : 'Internet is down. Changes cannot be saved until the connection is back.'}
    </p>
  );
}

/** "Training copy: made-up patients. Nothing here is real." (#5, MIG-03). */
export function TrainingBanner() {
  const { me } = useSession();
  if (env.appEnv !== 'training' && me?.appEnv !== 'training') return null;
  return (
    <p className={styles.training} role="note">
      <Settings size={15} aria-hidden="true" />
      Training copy: made-up patients. Nothing here is real.
    </p>
  );
}
